import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import { Upload } from '@aws-sdk/lib-storage';
import { S3Client } from '@aws-sdk/client-s3';

const defaultSchedule = [
  {
    timeOfDay: 'Morning Breakfast',
    checked: false,
    startTime: '',
    endTime: '',
    startAP: 'AM',
    endAP: 'AM',
  },
  {
    timeOfDay: 'Afternoon Lunch',
    checked: false,
    startTime: '',
    endTime: '',
    startAP: 'AM',
    endAP: 'AM',
  },
  {
    timeOfDay: 'Night Dinner',
    checked: false,
    startTime: '',
    endTime: '',
    startAP: 'AM',
    endAP: 'AM',
  },
];

const EditDialog = ({ open, foodId, onClose, onSave, apiUrl }) => {
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    desc: '',
    category: '',
    price: '',
    addons: [{ id: 1, name: '', addOnPrice: '' }],
    specialRequest: [{ id: 1, name: '', addOnPrice: '' }],
    extraRequest: [{ id: 1, name: '', addOnPrice: '' }],
    schedulingEnabled: false,
    schedule: defaultSchedule,
    img: '',
  });

  const categories = [
    'Main course',
    'Side Dish',
    'Snacks',
    'Desserts',
    'Appetizer',
    'Alcohol',
    'Drinks',
  ];

  const resetForm = useCallback(() => {
    setLoading(false);
    setIsUploading(false);
    setUploadStatus(null);
    setFile(null);
    setPreviewUrl('');
    setFormData({
      title: '',
      desc: '',
      category: '',
      price: '',
      addons: [{ id: 1, name: '', addOnPrice: '' }],
      specialRequest: [{ id: 1, name: '', addOnPrice: '' }],
      extraRequest: [{ id: 1, name: '', addOnPrice: '' }],
      schedulingEnabled: false,
      schedule: defaultSchedule,
      img: '',
    });
  }, []);

  const mapRequestArray = (arr = []) => {
    if (!arr.length) return [{ id: 1, name: '', addOnPrice: '' }];
    return arr.map((item, index) => ({
      id: index + 1,
      name: item.name || '',
      addOnPrice: item.addOnPrice ?? '',
    }));
  };

  const mapSchedule = (apiSchedule = []) => {
    return defaultSchedule.map((defaultItem) => {
      const matched = apiSchedule.find(
        (s) => s.timeOfDay === defaultItem.timeOfDay
      );

      if (matched) {
        return {
          timeOfDay: matched.timeOfDay || defaultItem.timeOfDay,
          checked: matched.checked ?? true,
          startTime: matched.startTime || '',
          endTime: matched.endTime || '',
          startAP: matched.startAP || 'AM',
          endAP: matched.endAP || 'AM',
        };
      }

      return defaultItem;
    });
  };

  const fetchFoodData = useCallback(async () => {
    setLoading(true);
    try {
      const storedToken = localStorage.getItem('accessToken');

      const response = await fetch(`${apiUrl}/foods/${foodId}`, {
        headers: {
          Authorization: `Bearer ${storedToken}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        setFormData({
          title: data.title || '',
          desc: data.desc || '',
          category: data.categories?.[0] || '',
          price: data.price || '',
          addons: mapRequestArray(data.addons),
          specialRequest: mapRequestArray(data.specialRequest),
          extraRequest: mapRequestArray(data.extraRequest),
          schedulingEnabled: data.schedulingEnabled || false,
          schedule: mapSchedule(data.schedule || []),
          img: data.img || '',
        });

        setPreviewUrl(data.img || '');
      } else {
        alert('Failed to fetch food details');
        onClose();
      }
    } catch (error) {
      console.error('Error fetching food:', error);
      alert('Error loading food data');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [apiUrl, foodId, onClose]);

  useEffect(() => {
    if (open && foodId) {
      fetchFoodData();
    } else {
      resetForm();
    }
  }, [open, foodId, fetchFoodData, resetForm]);

  const addRequest = (type) => {
    const newItem = {
      id: formData[type].length + 1,
      name: '',
      addOnPrice: '',
    };

    setFormData((prev) => ({
      ...prev,
      [type]: [...prev[type], newItem],
    }));
  };

  const deleteRequest = (type, id) => {
    const updated = formData[type].filter((item) => item.id !== id);

    setFormData((prev) => ({
      ...prev,
      [type]: updated.length ? updated : [{ id: 1, name: '', addOnPrice: '' }],
    }));
  };

  const handleRequestChange = (type, id, field, value) => {
    const updated = formData[type].map((item) =>
      item.id === id ? { ...item, [field]: value } : item
    );

    setFormData((prev) => ({
      ...prev,
      [type]: updated,
    }));
  };

  const handleScheduleCheck = (index, checked) => {
    const updatedSchedule = formData.schedule.map((item, i) =>
      i === index ? { ...item, checked } : item
    );

    setFormData((prev) => ({
      ...prev,
      schedule: updatedSchedule,
    }));
  };

  const updateScheduleField = (index, field, value) => {
    const updatedSchedule = formData.schedule.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );

    setFormData((prev) => ({
      ...prev,
      schedule: updatedSchedule,
    }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);

    if (selectedFile) {
      const objectUrl = URL.createObjectURL(selectedFile);
      setPreviewUrl(objectUrl);
    } else {
      setPreviewUrl(formData.img || '');
    }
  };

  const uploadToS3 = async (selectedFile) => {
    try {
      const fileName = `${Date.now()}-${selectedFile.name.replace(/\s+/g, '-')}`;
      const key = `food/${fileName}`;

      const credentials = {
        accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
        secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
      };

      const s3Client = new S3Client({
        region: process.env.REACT_APP_AWS_REGION,
        credentials,
      });

      const parallelUploadS3 = new Upload({
        client: s3Client,
        params: {
          Bucket: process.env.REACT_APP_S3_BUCKET,
          Key: key,
          Body: selectedFile,
          ContentType: selectedFile.type,
        },
        tags: [],
        queueSize: 4,
        partSize: 5 * 1024 * 1024,
      });

      parallelUploadS3.on('httpUploadProgress', (progress) => {
        const percentage = Math.round((progress.loaded / progress.total) * 100);
        setUploadStatus(`Uploading... ${percentage}%`);
      });

      await parallelUploadS3.done();

      const s3Url = `https://${process.env.REACT_APP_S3_BUCKET}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${key}`;
      setUploadStatus('Upload successful!');
      return s3Url;
    } catch (error) {
      console.error('S3 Upload Error:', error);
      setUploadStatus(`Upload failed: ${error.message}`);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.category || !formData.price) {
      alert('Please fill in required fields: Title, Category, and Price');
      return;
    }

    try {
      setIsUploading(true);

      let imageUrl = formData.img;

      if (file) {
        setUploadStatus('Uploading image...');
        imageUrl = await uploadToS3(file);
      }

      const apiData = {
        title: formData.title,
        desc: formData.desc,
        categories: [formData.category],
        price: formData.price,
        addons: formData.addons.filter((item) => item.name.trim() !== ''),
        specialRequest: formData.specialRequest.filter(
          (item) => item.name.trim() !== ''
        ),
        extraRequest: formData.extraRequest.filter(
          (item) => item.name.trim() !== ''
        ),
        schedulingEnabled: formData.schedulingEnabled,
        schedule: formData.schedule
          .filter((item) => item.checked)
          .map((item) => ({
            timeOfDay: item.timeOfDay,
            checked: item.checked,
            startTime: item.startTime,
            endTime: item.endTime,
            startAP: item.startAP,
            endAP: item.endAP,
          })),
        img: imageUrl,
      };

      const storedToken = localStorage.getItem('accessToken');

      const response = await fetch(`${apiUrl}/foods/${foodId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${storedToken}`,
        },
        body: JSON.stringify(apiData),
      });

      const responseData = await response.json();

      if (response.ok) {
        onSave(responseData);
        onClose();
      } else {
        alert(`Update failed: ${responseData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating food:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsUploading(false);
      setUploadStatus(null);
    }
  };

  const renderRequestSection = (title, type, addButtonText) => (
    <div style={{ marginBottom: '1rem' }}>
      <h4>{title}</h4>
      {formData[type].map((item, index) => (
        <div
          key={item.id}
          style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '8px',
            alignItems: 'center',
          }}
        >
          <input
            type="text"
            value={item.name}
            onChange={(e) =>
              handleRequestChange(type, item.id, 'name', e.target.value)
            }
            placeholder={`${title} ${index + 1}`}
            style={{ flex: 2, padding: '8px' }}
          />
          <input
            type="number"
            value={item.addOnPrice}
            onChange={(e) =>
              handleRequestChange(type, item.id, 'addOnPrice', e.target.value)
            }
            placeholder="Price"
            style={{ flex: 1, padding: '8px' }}
          />
          {formData[type].length > 1 && (
            <Button
              type="button"
              onClick={() => deleteRequest(type, item.id)}
              variant="outlined"
              color="error"
              size="small"
            >
              Delete
            </Button>
          )}
        </div>
      ))}
      <Button
        type="button"
        onClick={() => addRequest(type)}
        variant="outlined"
        size="small"
      >
        {addButtonText}
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Food Item</DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <CircularProgress />
          </div>
        ) : (
          <form onSubmit={handleSubmit} id="edit-food-form">
            {uploadStatus && (
              <div
                style={{
                  marginBottom: '1rem',
                  padding: '0.5rem',
                  backgroundColor: uploadStatus.includes('failed')
                    ? '#ffebee'
                    : '#e8f5e8',
                  color: uploadStatus.includes('failed') ? '#c62828' : '#2e7d32',
                  borderRadius: '4px',
                }}
              >
                {uploadStatus}
              </div>
            )}

            <div className="formInput" style={{ marginBottom: '1rem' }}>
              <label>Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                required
                style={{ width: '100%', padding: '8px', marginTop: '4px' }}
              />
            </div>

            <div className="formInput" style={{ marginBottom: '1rem' }}>
              <label>Description</label>
              <input
                type="text"
                value={formData.desc}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, desc: e.target.value }))
                }
                style={{ width: '100%', padding: '8px', marginTop: '4px' }}
              />
            </div>

            <div className="formInput" style={{ marginBottom: '1rem' }}>
              <label>Category *</label>
              <select
                value={formData.category}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, category: e.target.value }))
                }
                required
                style={{ width: '100%', padding: '8px', marginTop: '4px' }}
              >
                <option value="" disabled>
                  Select a category
                </option>
                {categories.map((cat, idx) => (
                  <option key={idx} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            {renderRequestSection('Addons', 'addons', 'Add Addon')}
            {renderRequestSection(
              'Special Requests',
              'specialRequest',
              'Add Special Request'
            )}
            {renderRequestSection(
              'Extra Requests',
              'extraRequest',
              'Add Extra Request'
            )}

            <div className="formInput" style={{ marginBottom: '1rem' }}>
              <label>Price *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, price: e.target.value }))
                }
                required
                style={{ width: '100%', padding: '8px', marginTop: '4px' }}
              />
            </div>

            <div className="formInput" style={{ marginBottom: '1rem' }}>
              <label htmlFor="edit-file">
                Food Image <DriveFolderUploadIcon style={{ verticalAlign: 'middle' }} />
              </label>
              <input
                type="file"
                id="edit-file"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                accept="image/*"
              />

              {previewUrl && (
                <div style={{ marginTop: '8px' }}>
                  <img
                    src={previewUrl}
                    alt="Preview"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '150px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                    }}
                  />
                  {file && <p style={{ margin: '4px 0' }}>{file.name}</p>}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label>
                <input
                  type="checkbox"
                  checked={formData.schedulingEnabled}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      schedulingEnabled: e.target.checked,
                    }))
                  }
                />
                Enable Scheduling
              </label>

              {formData.schedulingEnabled && (
                <div style={{ marginTop: '1rem' }}>
                  {formData.schedule.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        marginBottom: '1rem',
                        border: '1px solid #eee',
                        padding: '12px',
                        borderRadius: '8px',
                      }}
                    >
                      <label>
                        <input
                          type="checkbox"
                          checked={item.checked}
                          onChange={(e) =>
                            handleScheduleCheck(index, e.target.checked)
                          }
                        />
                        {' '}{item.timeOfDay}
                      </label>

                      {item.checked && (
                        <div style={{ display: 'flex', gap: '1rem', marginTop: '8px', flexWrap: 'wrap' }}>
                          <div>
                            <label>Start Time</label>
                            <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                              <input
                                type="time"
                                value={item.startTime}
                                onChange={(e) =>
                                  updateScheduleField(index, 'startTime', e.target.value)
                                }
                                style={{ padding: '6px' }}
                              />
                              <select
                                value={item.startAP}
                                onChange={(e) =>
                                  updateScheduleField(index, 'startAP', e.target.value)
                                }
                                style={{ padding: '6px' }}
                              >
                                <option value="AM">AM</option>
                                <option value="PM">PM</option>
                              </select>
                            </div>
                          </div>

                          <div>
                            <label>End Time</label>
                            <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                              <input
                                type="time"
                                value={item.endTime}
                                onChange={(e) =>
                                  updateScheduleField(index, 'endTime', e.target.value)
                                }
                                style={{ padding: '6px' }}
                              />
                              <select
                                value={item.endAP}
                                onChange={(e) =>
                                  updateScheduleField(index, 'endAP', e.target.value)
                                }
                                style={{ padding: '6px' }}
                              >
                                <option value="AM">AM</option>
                                <option value="PM">PM</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isUploading}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="edit-food-form"
          variant="contained"
          disabled={loading || isUploading}
        >
          {isUploading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDialog;